import React, { useState } from "react";
import {
  Save,
  X,
  Upload,
  FileText,
  AlertCircle,
  Users,
  Radio,
} from "lucide-react";

export default function CreateNtpRequestForm() {
  const [formData, setFormData] = useState({
    Project_Number__c: "",
    Carrier_Site_ID__c: "",
    Ground_NTP_Requested__c: "",
    Tower_NTP_Requested__c: "",
    First_Name__c: "",
    Last_Name__c: "",
    Company__c: "",
    Carrier__c: "",
    Email__c: "",
    Phone_Number__c: "",
    GC_Company__c: "",
    GC_First_Name__c: "",
    GC_Last_Name__c: "",
    GC_Email__c: "",
    GC_Phone_Number__c: "",
    Ground_Construction_Start_Date__c: null,
    Ground_Construction_End_Date__c: null,
    Tower_Construction_Start_Date__c: null,
    Tower_Construction_End_Date__c: null,
    NTP_Requirements_Acknowledged__c: false,
    Requestors_Initials__c: "",
    NTP_Request_Subtype__c: "",
  });

  const NTP_REQUEST_SUBTYPE_GROUND = [
    "Fiber Only", "Generator", "Ground Equipment",
  ];

  const NTP_REQUEST_SUBTYPE_TOWER = [
    "Lines and Antennas", "Microwave",
  ];

  const ntpSelected = () => requestTypeIsCivilGround() || requestTypeIsTower();

  const API_BASE_URL = process.env.REACT_APP_FUNCTION_APP_URL;
  const FUNCTION_KEY = process.env.REACT_APP_FUNCTION_KEY;
  const [gcInfoSameAsReq, setGcInfoSameAsReq] = useState(false);
  const [totalFileSize, setTotalFileSize] = useState(false);
  const formTitle = "NTP Request Form";
  const basicInformation = "Requestor Information";
  const gcInformation = "GC Information";
  const [formLoadTime, setFormLoadTime] = useState(Date.now());
  const [errorMessage, setErrorMessage] = useState({ text: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formLocked, setFormLocked] = useState(false);
  const [ntpRequestCreationError, setNtpRequestCreationError] = useState(false);
  const [ntpRequestName, setNtpRequestName] = useState(false);
  const [attachments, setAttachments] = useState([
    {
      id: "buildPermit",
      files: [],
      name: "Building Permit / No BP Letter",
      groundNtpFile: true,
      towerNtpFile: true,
      optionalFile: false,
      requiredFileGroundNtp: true,
      requiredFileTowerNtp: true,
    },
    {
      id: "electricalPermit",
      files: [],
      name: "Electrical Permit / No EP Letter",
      groundNtpFile: true,
      towerNtpFile: true,
      optionalFile: false,
      requiredFileGroundNtp: true,
      requiredFileTowerNtp: true,
    },
    {
      id: "zoningApproval",
      files: [],
      name: "Zoning Approval / No ZA Letter",
      groundNtpFile: true,
      towerNtpFile: true,
      optionalFile: false,
      requiredFileGroundNtp: true,
      requiredFileTowerNtp: true,
    },
    {
      id: "tenantFinalCxDrawings",
      files: [],
      name: "Tenant Final Construction Drawings",
      groundNtpFile: true,
      towerNtpFile: true,
      optionalFile: false,
      requiredFileGroundNtp: true,
      requiredFileTowerNtp: true,
    },
    {
      id: "cxContractorCoi",
      files: [],
      name: "Construction Contractor COI",
      groundNtpFile: true,
      towerNtpFile: true,
      optionalFile: false,
      requiredFileGroundNtp: true,
      requiredFileTowerNtp: true,
    },
    {
      id: "safetyCertificates",
      files: [],
      name: "Safety Certificates",
      groundNtpFile: true,
      towerNtpFile: true,
      optionalFile: false,
      requiredFileGroundNtp: true,
      requiredFileTowerNtp: true,
    },
    {
      id: "riggingPlan",
      files: [],
      name: "Rigging Plan",
      groundNtpFile: false,
      towerNtpFile: true,
      optionalFile: false,
      requiredFileGroundNtp: false,
      requiredFileTowerNtp: true,
    },
    {
      id: "cranePermit",
      files: [],
      name: "Crane Permit",
      groundNtpFile: false,
      towerNtpFile: true,
      optionalFile: true,
      requiredFileGroundNtp: false,
      requiredFileTowerNtp: true,
    },
    {
      id: "faaDeterminationLetter",
      files: [],
      name: "FAA Determination Letter",
      groundNtpFile: false,
      towerNtpFile: true,
      optionalFile: true,
      requiredFileGroundNtp: false,
      requiredFileTowerNtp: true,
    },
    {
      id: "slaAmendment",
      files: [],
      name: "SLA/Amendment",
      groundNtpFile: true,
      towerNtpFile: true,
      optionalFile: true,
      requiredFileGroundNtp: true,
      requiredFileTowerNtp: true,
    },
  ]);

  const fileMessage = "File types accepted: PDF, ZIP. Maximum file size: 30 MB";

  const showSubmitButton = () => {
    return !isSubmitting && !ntpRequestName && !formLocked;
  };

  const requestTypeIsTower = () => {
    return formData.Tower_NTP_Requested__c;
  };

  const requestTypeIsCivilGround = () => {
    return formData.Ground_NTP_Requested__c;
  };

  const handleFileRequirementUpdate = (e, fileType) => {
    const fileRequired = !e.target.checked;
    console.log(fileRequired);

    const newAttachments = attachments.map((attachment) => {
      if (attachment.id === fileType) {
        if (requestTypeIsTower()) {
          attachment.requiredFileTowerNtp = fileRequired;
        }
        if (requestTypeIsCivilGround()) {
          attachment.requiredFileGroundNtp = fileRequired;
        }
      }
      return attachment;
    });
    setAttachments([...newAttachments]);
    console.log(JSON.stringify(attachments));
  };

  const getFileRequiredDefaultValue = (fileType) => {
    let fileRequired;

    if (requestTypeIsTower()) {
      fileRequired = fileType.requiredFileTowerNtp;
    } else if (requestTypeIsCivilGround()) {
      fileRequired = fileType.requiredFileGroundNtp;
    }

    return fileRequired;
  };

  const handleFileUpload = (e, fileType) => {
    const uploadedfiles = e.target.files;
    const maxSize = 30 * 1024 * 1024; // 30 MB in bytes
    const maxTotalFileSize = 99 * 1024 * 1024;
    const validFiles = [];
    const invalidFiles = [];

    let tempTotalFileSize = totalFileSize;
    Array.from(uploadedfiles).forEach((file) => {
      if (file.size > maxSize) {
        invalidFiles.push(file.name);
      } else if (tempTotalFileSize + file.size > maxTotalFileSize) {
        alert(
          `File(s) can't be uploaded b/c the total size of all files cannot exceed 99 MB`,
        );
        return;
      } else {
        validFiles.push(file);
        tempTotalFileSize += file.size;
      }
      console.log(tempTotalFileSize);
    });

    setTotalFileSize(tempTotalFileSize);
    console.log(totalFileSize);

    if (invalidFiles.length > 0) {
      alert(
        `The following files exceed 30 MB and were not uploaded:\n${invalidFiles.join(
          "\n",
        )}`,
      );
    } else {
      const newAttachments = attachments.map((attachment) => {
        if (attachment.id === fileType) {
          let allFiles = attachment.files.concat(validFiles);
          attachment.files = allFiles;
        }
        return attachment;
      });
      setAttachments([...newAttachments]);
    }

    // this ensures that if the user then removes a file and then tries to upload the same file that it will be allowed
    e.target.value = "";
  };

  const removeFile = (fileType, index = null) => {
    const newAttachments = attachments.map((attachment) => {
      if (attachment.id === fileType) {
        let existingFiles = attachment.files;
        existingFiles.splice(index, 1);
        return { ...attachment, value: existingFiles }; // Create a new attachment object with the file removed
      } else {
        return attachment;
      }
    });
    setAttachments(newAttachments);
  };

  const handleChange = (e) => {
    let name = e.target.name;
    let value;
    if (e.target.type === "checkbox") {
      value = e.target.checked;
    } else {
      value = e.target.value;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBlur = (e) => {
    if (e.target.type !== "checkbox") {
      const name = e.target.name;
      setFormData((prev) => ({
        ...prev,
        [name]: typeof prev[name] === "string" ? prev[name].trim() : prev[name],
      }));
    }
  };

  const handleGcInfoSameAsRequestorInfo = (e) => {
    setGcInfoSameAsReq(e.target.checked);
  };

  const handleWebsiteUpdated = () => {
    // website is a hidden field that should never be updated so if it
    // is updated then lock the form
    setFormLocked(true);
  };

  const validateProjectNumber = (projectNumber) => {
    const pattern = /^P-\d{6}$/;
    return pattern.test(projectNumber);
  };

  const validateEmail = (email) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(email);
  };

  const validateRequiredFiles = () => {
    let filesMissing = false;
    let fileNamesMissing = [];
    let errorString = "";

    //loop through the attachments and if they are null and required then capture the error details

    for (const fileType of attachments) {
      if (
        fileType.files != null &&
        fileType.files.length === 0 &&
        ((fileType.requiredFileGroundNtp === true &&
          requestTypeIsCivilGround()) ||
          (fileType.requiredFileTowerNtp === true && requestTypeIsTower()))
      ) {
        filesMissing = true;
        fileNamesMissing.push(fileType.name);
      }
    }

    if (filesMissing) {
      errorString =
        "The following files must be uploaded: \n" +
        fileNamesMissing.join(", ");
    }

    return {
      filesMissing: filesMissing,
      fileNamesMissing: fileNamesMissing,
      errorString: errorString,
    };
  };

  async function createNtpRequest(event) {
    event.preventDefault();

    const timeElapsed = (Date.now() - formLoadTime) / 1000;

    if (timeElapsed < 2) {
      setErrorMessage({
        text: "Form submitted too quickly.",
      });
      setFormLocked(true);
      return;
    }

    //reset error message on each submission
    setErrorMessage({ text: "" });

    if (!formData.Ground_NTP_Requested__c && !formData.Tower_NTP_Requested__c) {
      setErrorMessage({
        text: "You must select the NTP Type(s) you are requesting",
      });
      return;
    }

    if (
      formData.Project_Number__c != "" &&
      !validateProjectNumber(formData.Project_Number__c)
    ) {
      setErrorMessage({
        text: "Project Number must be in format P-XXXXXX (e.g., P-000001)",
      });
      return;
    }

    if (formData.Email__c != "" && !validateEmail(formData.Email__c)) {
      setErrorMessage({
        text: "Email must include a domain (e.g., test@test.com)",
      });
      return;
    }

    if (validateRequiredFiles().filesMissing) {
      setErrorMessage({
        text: validateRequiredFiles().errorString,
      });
      return;
    }

    setIsSubmitting(true);

    //copy the gc info to the req info
    if (gcInfoSameAsReq) {
      formData.GC_Company__c = formData.Company__c;
      formData.GC_First_Name__c = formData.First_Name__c;
      formData.GC_Last_Name__c = formData.Last_Name__c;
      formData.GC_Email__c = formData.Email__c;
      formData.GC_Phone_Number__c = formData.Phone_Number__c;
    }

    try {
      //convert all uploaded files to base64
      let filesToIntegrate = [];
      for (const fileType of attachments) {
        if (fileType.files != null && fileType.files.length > 0) {
          for (const file of fileType.files) {
            let base64File = await fileToBase64(file);
            let fileObj = {
              fileName: file.name,
              base64File: base64File,
              ntpRequestFileType: fileType.name,
            };
            filesToIntegrate.push(fileObj);
          }
        }
      }

      const createNtpRequestResult = await fetch(API_BASE_URL, {
        method: "POST",
        headers: {
          "Ocp-Apim-Subscription-Key": FUNCTION_KEY,
        },
        body: JSON.stringify({
          ntpRequest: formData,
          files: filesToIntegrate,
        }),
      });

      if (createNtpRequestResult.status === 200) {
        const data = await createNtpRequestResult.json();
        setNtpRequestName(data.Name);
        setIsSubmitting(false);
      } else {
        const data = await createNtpRequestResult.json();
        setErrorMessage({
          text: `Please share these technical details with your Tillman contact: ${data.message || "Error: " + createNtpRequestResult.status}`,
        });
        setNtpRequestCreationError(true);
        setIsSubmitting(false);
      }
    } catch (error) {
      setErrorMessage({
        text: `Please share these technical details with your Tillman contact: ${error.message}`,
      });
      setNtpRequestCreationError(true);
      setIsSubmitting(false);
    }
  }

  function fileToBase64(fileContent) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(fileContent);
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <div className="flex justify-center">
            <img
              src="/tillman-logo.png"
              alt="Company Logo"
              className="h-20 w-auto object-contain"
            />
          </div>
          <div className="text-center bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 gap-3">
            <h1 className="text-center text-2xl font-bold text-white">
              {formTitle}
            </h1>
          </div>

          <form onSubmit={createNtpRequest} className="p-6 space-y-6">
            {/* Request Details */}
            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200">
                Request Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tillman Project Number <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="Project_Number__c"
                    value={formData.Project_Number__c}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={ntpRequestName}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    placeholder="P-000001"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format: P-XXXXXX (e.g., P-000001)
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    NTPs Requested <span className="required">*</span>
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="Ground_NTP_Requested__c"
                        value={formData.Ground_NTP_Requested__c}
                        onChange={handleChange}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Ground</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="Tower_NTP_Requested__c"
                        value={formData.Tower_NTP_Requested__c}
                        onChange={handleChange}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Tower</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    If requesting both Ground & Tower NTPs then select both
                  </p>
                </div>

                <div></div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      NTP Subtype<span className="required">*</span>
                    </label>
                      <select
                        name="NTP_Request_Subtype__c"
                        value={formData.NTP_Request_Subtype__c}
                        onChange={handleChange}
                        disabled={!ntpSelected() || ntpRequestName}
                        required={!!ntpSelected()}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        <option value="">-- Select --</option>
                        {(requestTypeIsCivilGround() && requestTypeIsTower()
                          ? [...NTP_REQUEST_SUBTYPE_GROUND, ...NTP_REQUEST_SUBTYPE_TOWER]
                          : requestTypeIsCivilGround()
                            ? NTP_REQUEST_SUBTYPE_GROUND
                            : requestTypeIsTower()
                              ? NTP_REQUEST_SUBTYPE_TOWER
                              :[]
                        ).map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Select at least one NTP above to enable
                          </p>
                      </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Carrier <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="Carrier__c"
                    value={formData.Carrier__c}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={ntpRequestName}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Carrier Site ID <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="Carrier_Site_ID__c"
                    value={formData.Carrier_Site_ID__c}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={ntpRequestName}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </section>

            {/* Basic Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200">
                {basicInformation}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Honeypot field - hidden from users */}
                <input
                  type="textarea"
                  name="website"
                  onChange={handleWebsiteUpdated}
                  className="absolute opacity-0 pointer-events-none"
                  tabIndex="-1"
                  autoComplete="new-password"
                />

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="Company__c"
                    value={formData.Company__c}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={ntpRequestName}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="First_Name__c"
                    value={formData.First_Name__c}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={ntpRequestName}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name <span class="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="Last_Name__c"
                    value={formData.Last_Name__c}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={ntpRequestName}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span class="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="Email__c"
                    value={formData.Email__c}
                    onChange={handleChange}
                    disabled={ntpRequestName}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span class="required">*</span>
                  </label>
                  <input
                    type="phone"
                    name="Phone_Number__c"
                    value={formData.Phone_Number__c}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={ntpRequestName}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </section>

            {/* GC Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200">
                {gcInformation}
              </h2>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  onChange={handleGcInfoSameAsRequestorInfo}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  GC Information same as Requestor Information
                </span>
              </label>

              {!gcInfoSameAsReq && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <br></br>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GC Company <span class="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="GC_Company__c"
                      value={formData.GC_Company__c}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={ntpRequestName}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GC First Name <span class="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="GC_First_Name__c"
                      value={formData.GC_First_Name__c}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={ntpRequestName}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GC Last Name <span class="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="GC_Last_Name__c"
                      value={formData.GC_Last_Name__c}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={ntpRequestName}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GC Email <span class="required">*</span>
                    </label>
                    <input
                      type="email"
                      name="GC_Email__c"
                      value={formData.GC_Email__c}
                      onChange={handleChange}
                      disabled={ntpRequestName}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GC Phone Number <span class="required">*</span>
                    </label>
                    <input
                      type="phone"
                      name="GC_Phone_Number__c"
                      value={formData.GC_Phone_Number__c}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={ntpRequestName}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </section>

            {/* Ground Construction Schedule */}
            {requestTypeIsCivilGround() && (
              <section>
                <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200">
                  Ground Construction Schedule
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ground Construction Start Date{" "}
                      <span class="required">*</span>
                    </label>
                    <input
                      type="date"
                      name="Ground_Construction_Start_Date__c"
                      value={formData.Ground_Construction_Start_Date__c}
                      onChange={handleChange}
                      disabled={ntpRequestName}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ground Construction End Date{" "}
                      <span class="required">*</span>
                    </label>
                    <input
                      type="date"
                      name="Ground_Construction_End_Date__c"
                      value={formData.Ground_Construction_End_Date__c}
                      onChange={handleChange}
                      disabled={ntpRequestName}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </section>
            )}

            {/* Tower Construction Schedule */}
            {requestTypeIsTower() && (
              <section>
                <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200">
                  Tower Construction Schedule
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tower Construction Start Date{" "}
                      <span class="required">*</span>
                    </label>
                    <input
                      type="date"
                      name="Tower_Construction_Start_Date__c"
                      value={formData.Tower_Construction_Start_Date__c}
                      onChange={handleChange}
                      disabled={ntpRequestName}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tower Construction End Date{" "}
                      <span class="required">*</span>
                    </label>
                    <input
                      type="date"
                      name="Tower_Construction_End_Date__c"
                      value={formData.Tower_Construction_End_Date__c}
                      onChange={handleChange}
                      disabled={ntpRequestName}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </section>
            )}

            {(requestTypeIsCivilGround() || requestTypeIsTower()) && (
              <section>
                <div className="min-h-screen bg-gray-100 py-8 px-4">
                  <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8">
                    <h1 className="text-3xl font-bold text-gray-800 border-b-4 border-blue-500 pb-3 mb-6">
                      Crew Certification Requirements
                    </h1>

                    <p className="text-gray-700 mb-8">
                      The following are minimum certification requirements for
                      any personnel working on Tillman Infrastructure sites. The
                      minimum required certifications to be submitted and
                      approved include:
                    </p>

                    {/* Civil Crew Section */}
                    <div className="mb-8">
                      <div className="flex items-center mb-4">
                        <Users className="w-6 h-6 text-blue-600 mr-2" />
                        <h2 className="text-2xl font-bold text-gray-900">
                          Civil Crew Only:
                        </h2>
                      </div>

                      <ul className="space-y-3 ml-8">
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-3">•</span>
                          <span className="text-gray-700">
                            OSHA 30 DOL card for the foreman/competent person
                            onsite
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-3">•</span>
                          <span className="text-gray-700">
                            OSHA 10 DOL card for each additional employee onsite
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-3">•</span>
                          <span className="text-gray-700">
                            A minimum of 2 CPR/First Aid/BBP acceptable
                            certified employees onsite at all times.
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-3">•</span>
                          <span className="text-gray-700">
                            Lull/Forklift/Manlift/Heavy Equipment Operator
                            Training Certification if applicable.
                          </span>
                        </li>
                      </ul>
                    </div>

                    {/* Tower Crew Section */}
                    <div className="mb-8">
                      <div className="flex items-center mb-4">
                        <Radio className="w-6 h-6 text-green-600 mr-2" />
                        <h2 className="text-2xl font-bold text-gray-900">
                          Tower Crew:
                        </h2>
                      </div>

                      <ul className="space-y-3 ml-8">
                        <li className="flex items-start">
                          <span className="text-green-600 mr-3">•</span>
                          <span className="text-gray-700">
                            OSHA 30 DOL card for the foreman/competent person
                            onsite
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-3">•</span>
                          <span className="text-gray-700">
                            OSHA 10 DOL card for each additional employee onsite
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-3">•</span>
                          <span className="text-gray-700">
                            A minimum of 2 CPR/First Aid/BBP acceptable
                            certified employees onsite at all times.
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-3">•</span>
                          <span className="text-gray-700">
                            Lull/Forklift/Manlift/Heavy Equipment Operator
                            Training Certification if applicable.
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-3">•</span>
                          <span className="text-gray-700">
                            A minimum of 2 Competent Climber/Rescue
                            certifications if any elevated work is to be
                            performed.
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-3">•</span>
                          <span className="text-gray-700">
                            A minimum of an Authorized Climber/Rescue
                            certification for any additional climber if elevated
                            work is to be performed.
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-3">•</span>
                          <span className="text-gray-700">
                            RF Awareness Certification for personnel working in
                            the RF environment with certs on site at all times.
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-3">•</span>
                          <span className="text-gray-700">
                            Competent Rigger certification if any rigging is to
                            be performed.
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-3">•</span>
                          <span className="text-gray-700">
                            Rigging Plan if any rigging is to be performed
                            onsite as outlined in ANSI A10.48, signed by the
                            Competent Rigger.
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-3">•</span>
                          <span className="text-gray-700">
                            Capstan Hoist Operator Certification if applicable.
                          </span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-green-600 mr-3">•</span>
                          <span className="text-gray-700">
                            Gin Pole Operator Certification if a Gin pole will
                            be used.
                          </span>
                        </li>
                      </ul>
                    </div>

                    {/* Important Notes Section */}

                    <div className="bg-red-50 border-l-4 border-red-400 p-6">
                      <div className="flex items-start">
                        <AlertCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2">
                            NOTE:
                          </h3>
                          <p className="text-gray-700 mb-3">
                            Tillman Infrastructure does not accept in-house
                            certifications regardless of prior approval from
                            anyone outside of Tillman Infrastructure. ALL
                            CPR/First Aid/AED Certifications must be through
                            recognized American Heart Association Accredited
                            training centers and MUST include proof of hands-on
                            practical skills evaluation.
                          </p>
                          <p className="text-gray-700 mb-3">
                            Certifications must be submitted, reviewed, and
                            approved within 3 days of starting work on site.
                            Non-certified personnel will not be allowed to enter
                            worksites and perform work tasks. All certifications
                            must be onsite at all times and be available to any
                            Tillman Representative, along with each daily
                            JSA/JHA and Site-Specific Emergency Action Plan
                            readily available to all crew members and signed by
                            every person onsite.
                          </p>
                          <p className="text-gray-700 mb-3">
                            Alone work is not permitted under our CPR
                            requirements without approval from our Safety Team
                            and Operations Teams on a per project basis.
                          </p>
                          <p className="text-gray-700">
                            Upon completion of each project, the vendor will
                            submit each separate JSA/JHA for every day onsite
                            for the respective project for review and approval.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* NTP Requirements Section (Tower Projects Only) */}
            {requestTypeIsTower() && (
              <section>
                <div className="min-h-screen bg-gray-100 py-8 px-4">
                  <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8">
                    <h1 className="text-3xl font-bold text-gray-800 border-b-4 border-blue-500 pb-3 mb-6">
                      Tower NTP Requirements
                    </h1>

                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                      <p className="mb-2">
                        <strong>Important:</strong> It is the General
                        Contractor's (GC's) responsibility to ensure that all
                        required FAA documentation is filed, accurate, and
                        specific to the project before an NTP can be issued and
                        any work begins.
                      </p>
                      <p
                        style={{
                          color: "#dc2626",
                          fontWeight: 600,
                        }}
                      >
                        <strong>
                          Failure to file for FAA Determination when required
                          can result in stop-work orders, fines, or liability
                          for non-compliance with federal law.
                        </strong>
                      </p>
                      <br></br>
                      <p className="font-semibold mb-2">
                        Please note that in addition to FAA requirements:
                      </p>
                      <ul className="list-disc ml-6 space-y-2">
                        <li>
                          <strong>
                            Local / Municipal Permits may be required, even if
                            FAA is not:
                          </strong>
                          <ul className="list-disc ml-6 mt-2 space-y-1">
                            <li>
                              Some cities and counties require a crane operation
                              permit regardless of FAA filing.
                            </li>
                            <li>
                              Building permits or right-of-way permits may be
                              necessary if the crane setup impacts traffic,
                              sidewalks, or public areas.
                            </li>
                          </ul>
                        </li>
                        <li>
                          <strong>
                            Department of Transportation (DOT) Permits may also
                            apply:
                          </strong>
                          <ul className="list-disc ml-6 mt-2 space-y-1">
                            <li>
                              If your crane, hoist, or transport equipment will
                              operate in or obstruct any public roadways, DOT
                              clearance or permits may be required.
                            </li>
                            <li>
                              It is the GC's responsibility to check with local
                              and state DOT offices and obtain any required
                              permits or approvals.
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </div>

                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                      Required Documentation:
                    </h2>

                    <ol className="list-decimal ml-6 space-y-4">
                      <li>Need to have an SLA or Amendment fully executed</li>

                      <li>
                        Copy of Building Permit/Electrical Permit/Zoning
                        approval or no Permitting/Zoning letter
                      </li>

                      <li>Tenant Construction Drawings</li>

                      <li>
                        Construction Contractor Certificate of Insurance with
                        Tillman Infrastructure, LLC as Certificate Holder
                        (sample attached)
                      </li>

                      <li>GC Contact Information</li>

                      <li>
                        Construction schedule (estimated start and completion
                        date)
                      </li>

                      <li>Safety requirements/certs (requirements attached)</li>

                      <li>
                        <strong>Crane Permit:</strong> Per FAA and FCC
                        regulations, submission of FAA Form 7460-1 (Notice of
                        Proposed Construction or Alteration) is mandatory when
                        equipment exceeds tower structure.
                      </li>

                      <li>
                        <strong>If a crane will be used</strong> - FAA
                        Determination is required for any crane that exceeds the
                        tower structure.
                        <ol className="list-decimal ml-6 mt-2 space-y-2">
                          <li>
                            The GC will be responsible for obtaining the FAA
                            Determination for this specific project and
                            submitting the determination letter as part of the
                            NTP requirements.
                          </li>
                          <li>
                            If already filed, please submit the FAA
                            Determination Letter specific to this project.
                          </li>
                          <li>
                            For public or private airports where an FAA
                            Determination is not required, the GC must:
                            <ol className="list-decimal ml-6 mt-2 space-y-1">
                              <li>
                                File a NOTAM (Notice to Air Missions) with the
                                FAA
                              </li>
                              <li>
                                Directly notify the airport operator of the
                                planned crane use and operation dates
                              </li>
                            </ol>
                          </li>
                          <li>Provide a Rigging Plan</li>
                        </ol>
                      </li>

                      <li>
                        <strong>If a crane is not being used:</strong>
                        <ol className="list-decimal ml-6 mt-2">
                          <li>
                            The contractor must still provide a rigging plan, or
                            any hoist mechanism plans for Safety to review and
                            approve
                          </li>
                        </ol>
                      </li>

                      <li>
                        <strong>
                          Competent Rigger Approval and Plan Updates
                        </strong>
                        <ol className="list-decimal ml-6 mt-2 space-y-2">
                          <li>
                            Rigging plan needs to have the Competent person
                            signature on it, no one else is allowed to sign for
                            the competent rigger
                          </li>
                          <li>
                            If the competent rigger or plan details change from
                            the time the original plan is submitted, it must be
                            resubmitted to Tillman prior to work starting. We
                            understand sometimes this is just a couple days
                            before or the day of due to last minute scheduling
                            in the industry, but it is imperative to not start
                            working until sent in.
                          </li>
                          <li>
                            If there are any crew changes or competent rigger
                            changes the certifications for those individuals
                            must be submitted prior to start.
                          </li>
                          <li>
                            This applies even if changes to crew or competent
                            rigger happen mid project, changes must be made and
                            amended documents sent in.
                          </li>
                        </ol>
                      </li>

                      <li>
                        <strong>
                          All FAA Determination filed by Tillman are specific to
                          the construction of Tillman's tower structures and may
                          not be used for any other project(s).
                        </strong>
                      </li>
                    </ol>

                    <br></br>
                  </div>
                </div>
              </section>
            )}

            {/* Required Files */}
            {(requestTypeIsCivilGround() || requestTypeIsTower()) && (
              <section>
                <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-blue-200">
                  {formData.Request_Type__c} Required NTP Files
                </h2>

                <div className="space-y-6">
                  {attachments.map((fileType) => (
                    <>
                      {((requestTypeIsCivilGround() &&
                        fileType.groundNtpFile) ||
                        (requestTypeIsTower() && fileType.towerNtpFile)) && (
                        <>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {fileType.name}{" "}
                                {((requestTypeIsCivilGround() &&
                                  fileType.requiredFileGroundNtp) ||
                                  (requestTypeIsTower() &&
                                    fileType.requiredFileTowerNtp)) && (
                                  <>
                                    <span className="text-red-500">*</span>
                                  </>
                                )}
                              </label>

                              {fileType.optionalFile && (
                                <>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      key={fileType.id}
                                      onChange={(e) =>
                                        handleFileRequirementUpdate(
                                          e,
                                          fileType.id,
                                        )
                                      }
                                      defaultValue={getFileRequiredDefaultValue(
                                        fileType,
                                      )}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-600">
                                      Not Applicable
                                    </span>
                                  </label>
                                </>
                              )}
                            </div>
                            {((requestTypeIsCivilGround() &&
                              fileType.requiredFileGroundNtp) ||
                              (requestTypeIsTower() &&
                                fileType.requiredFileTowerNtp)) && (
                              <>
                                <p className="text-xs text-gray-500 mb-2">
                                  {fileMessage}
                                </p>
                                <div className="flex items-center gap-3">
                                  <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                                    <Upload className="w-5 h-5 mr-2 text-gray-400" />
                                    <span className="text-sm text-gray-600">
                                      Add Files
                                    </span>

                                    <input
                                      key={fileType.id}
                                      type="file"
                                      multiple
                                      onChange={(e) =>
                                        handleFileUpload(e, fileType.id)
                                      }
                                      className="hidden"
                                      disabled={ntpRequestName}
                                      accept=".pdf,.zip"
                                    />
                                  </label>
                                </div>
                                {fileType.files.length > 0 && (
                                  <div className="mt-2 space-y-2">
                                    {fileType.files.map((file, index) => (
                                      <div
                                        key={index}
                                        className="flex items-center justify-between p-3 bg-blue-50 rounded-md border border-blue-200"
                                      >
                                        <div className="flex items-center gap-2">
                                          <FileText
                                            className="text-blue-600"
                                            size={20}
                                          />
                                          <div>
                                            <p className="text-sm font-medium text-gray-800">
                                              {file.name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              {(file.size / 1024).toFixed(2)} KB
                                            </p>
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            removeFile(fileType.id, index)
                                          }
                                          className="text-red-500 hover:text-red-700 transition-colors"
                                        >
                                          <X size={18} />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  ))}
                </div>
              </section>
            )}

            <div
              className="form-group"
              style={{
                backgroundColor: "#fef3c7",
                padding: "15px",
                borderRadius: "5px",
                border: "2px solid #f59e0b",
              }}
            >
              <div>
                <p
                  style={{
                    color: "#dc2626",
                    fontWeight: 600,
                  }}
                >
                  NTP deliverable requests should have ALL required
                  documents/information submitted in its entirety, otherwise the
                  request is deemed incomplete and will delay the release of
                  your NTP.
                </p>
                <br></br>
                <p
                  style={{
                    color: "#dc2626",
                    fontWeight: 600,
                  }}
                >
                  Tillman has 5 days from the date of receipt of all
                  deliverables to process NTPs. Please forecast start days no
                  sooner than 5 days from the date of request.
                </p>
                <br></br>
                <p
                  style={{
                    color: "#dc2626",
                    fontWeight: 600,
                  }}
                >
                  Additionally, no NTPs will be issued prior to the completion
                  of a Fully Executed SLA/Amendment
                </p>
              </div>
              <br></br>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                }}
              >
                <input
                  type="checkbox"
                  name="NTP_Requirements_Acknowledged__c"
                  value={formData.NTP_Requirements_Acknowledged__c}
                  onChange={handleChange}
                  disabled={ntpRequestName}
                  required
                />
                <div style={{ flex: 1 }}>
                  <label
                    htmlFor="ntpAgreement"
                    style={{
                      margin: 0,
                      fontWeight: 600,
                      color: "#92400e",
                    }}
                  >
                    <span className="required">*</span>I have read and agree to
                    all of the above Requirements{" "}
                  </label>
                  <div style={{ marginTop: "10px" }}>
                    <label
                      htmlFor="ntpInitials"
                      style={{
                        fontWeight: 500,
                        color: "#374151",
                      }}
                    >
                      Enter your initials to confirm agreement:
                    </label>
                    <input
                      type="text"
                      name="Requestors_Initials__c"
                      value={formData.Requestors_Initials__c}
                      onChange={handleChange}
                      disabled={ntpRequestName}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      placeholder="ABC"
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>
            </div>

            {errorMessage.text && (
              <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 rounded">
                <p className="text-sm text-red-800 whitespace-pre-line">
                  {errorMessage.text}
                </p>
              </div>
            )}            

            {showSubmitButton() && (
              <div>
                <div className="flex justify-center">
                  <button
                    type="submit"
                    disabled={isSubmitting || formLocked}
                    className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    <Save size={18} />
                    Submit NTP Request
                  </button>
                </div>
              </div>
            )}

            {/* Form Actions */}
            <div className="pt-4 border-t">
              {isSubmitting && (
                <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <p className="text-sm text-yellow-800 font-medium flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4 text-yellow-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Please wait... Your NTP Request is being saved. It may take
                    1-2 minutes for the save to complete. Do not leave or
                    refresh the page.
                  </p>
                </div>
              )}

              {ntpRequestName && (
                <div className="mb-4 p-3 bg-green-50 border-l-4 border-green-400 rounded">
                  <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                    NTP Created: {ntpRequestName}.
                  </p>
                </div>
              )}

              {ntpRequestCreationError && (
                <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 rounded">
                  <p className="text-sm text-red-800 font-medium flex items-center gap-2">
                    NTP Request Creation Failed
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
